
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Invitation {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'superadmin' | 'member';
  expires_at: string;
}

export const useInvitationAcceptance = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAcceptInvitation = async (invitation: Invitation) => {
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setIsSubmitting(true);
    try {
      // Check if user already exists first
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('email', invitation.email)
        .single();

      if (existingProfile) {
        
        // Just update the invitation status
        const { data: updateData, error: updateError } = await supabase.functions.invoke('update-invitation-status', {
          body: {
            invitation_id: invitation.id,
            status: 'accepted'
          }
        });

        if (updateError) {
          toast.error('Failed to accept invitation. Please try again.');
          return;
        }

        toast.success('Invitation accepted! You can now sign in with your existing account.');
        navigate('/auth?mode=signin');
        return;
      }
      
      const { data: userData, error: userError } = await supabase.functions.invoke('create-user', {
        body: {
          email: invitation.email,
          password,
          first_name: invitation.first_name,
          last_name: invitation.last_name,
          username: invitation.email,
          role: invitation.role
        }
      });

      if (userError) {
        
        // Handle specific error cases
        if (userError.message?.includes('already been registered') || userError.message?.includes('email_exists')) {
          toast.error('An account with this email already exists. Please sign in instead.');
          navigate('/auth?mode=signin');
          return;
        }
        
        throw new Error(userError.message || 'Failed to create user account');
      }

      if (userData?.error) {
        
        // Handle specific error cases from the server response
        if (userData.error.includes('already been registered') || userData.error.includes('email_exists')) {
          toast.error('An account with this email already exists. Please sign in instead.');
          navigate('/auth?mode=signin');
          return;
        }
        
        throw new Error(userData.error);
      }

      if (!userData?.success) {
        throw new Error('Failed to create user account - please try again');
      }

      const { data: updateData, error: updateError } = await supabase.functions.invoke('update-invitation-status', {
        body: {
          invitation_id: invitation.id,
          status: 'accepted'
        }
      });

      if (updateError) {
        toast.error('Account created but invitation status not updated. Please contact support.');
      }

      toast.success('Account created successfully! You can now sign in.');
      navigate('/auth?mode=signin');
      
    } catch (err: unknown) {
      console.error('Error accepting invitation:', err);
      const errorMessage = (err as Error)?.message || 'Failed to create account';
      
      // Handle specific error messages
      if (errorMessage.includes('already been registered') || errorMessage.includes('email_exists')) {
        toast.error('An account with this email already exists. Please sign in instead.');
        navigate('/auth?mode=signin');
      } else if (errorMessage.includes('role') || errorMessage.includes('Invalid role')) {
        toast.error('There was an issue with your invitation role. Please contact support.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    isSubmitting,
    handleAcceptInvitation
  };
};
